import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MoneyInput } from '../MoneyInput';
import { SearchableSelect } from '../SearchableSelect';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { ArrowUpCircle, ArrowDownCircle, Plus, Trash2, AlertTriangle, HandCoins, Building2, User, Wallet, Users } from 'lucide-react';
import { PayrollUploader } from '../PayrollUploader';

const today = () => new Date().toISOString().slice(0, 10);
const fmtCurrency = (amount, currency = 'ARS') =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);

export function CollectionDialog({
  open, onOpenChange, form, setForm, cashBanks, incomeConcepts, paymentTypes,
  residents = [], residentsAvailable = true,
  addPaymentDetail, updatePaymentDetail, removePaymentDetail,
  isCheckPaymentType, isTransferPaymentType = () => false,
  collectionTotal, detectCashIntoBankConflict, onSave, onCancel,
  residentPending = { invoices: [], available_advance: 0, total_outstanding: 0 },
  loadResidentPending, toggleResidentInvoice, updateResidentInvoiceAmount, residentAllocTotal = 0,
  submitting = false,
}) {
  const allocDiff = Math.round((residentAllocTotal - collectionTotal) * 100) / 100;
  const allocOver = form.payer_type === 'resident' && residentAllocTotal > 0 && allocDiff > 0.009;
  const isResident = form.payer_type === 'resident';
  const selectedResident = isResident ? residents.find(r => r.resident_id === form.resident_id) : null;
  // v3.36.4 — Filtrar payment_types según categorías permitidas del cash_bank seleccionado
  const selectedCashBankColl = cashBanks.find(cb => cb.cash_bank_id === form.cash_bank_id);
  const allowedCatsColl = selectedCashBankColl?.allowed_payment_categories ?? null;
  const availablePaymentTypesColl = (paymentTypes || []).filter(pt => {
    if (allowedCatsColl === null) return true;
    if (allowedCatsColl.length === 0) return false;
    return allowedCatsColl.includes((pt.category || '').toLowerCase());
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpCircle className="w-5 h-5 text-green-500" />
            Nueva Cobranza
          </DialogTitle>
          <DialogDescription>Registrá un ingreso de dinero y sus medios de pago.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Selector Tipo de Cobranza */}
          <div className="rounded-md border bg-muted/30 p-3">
            <Label className="text-sm font-semibold">Tipo de Cobranza</Label>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, payer_type: 'other', resident_id: '' })}
                className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-md border text-sm font-medium transition-colors ${!isResident ? 'bg-green-600 text-white border-green-600' : 'bg-background hover:bg-muted'}`}
                data-testid="payer-type-other-btn"
              >
                <Wallet className="w-4 h-4" /> Otra
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, payer_type: 'resident', income_concept_id: '', client_name: '' })}
                disabled={!residentsAvailable}
                title={!residentsAvailable ? 'No tenés permiso para ver residentes' : ''}
                className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-md border text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isResident ? 'bg-blue-600 text-white border-blue-600' : 'bg-background hover:bg-muted'}`}
                data-testid="payer-type-resident-btn"
              >
                <User className="w-4 h-4" /> Residente
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fecha *</Label>
              <Input type="date" max={today()} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label>Caja/Banco destino *</Label>
              <select value={form.cash_bank_id} onChange={(e) => setForm({ ...form, cash_bank_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">Seleccionar...</option>
                {cashBanks.map(cb => <option key={cb.cash_bank_id} value={cb.cash_bank_id}>{cb.name} ({cb.currency})</option>)}
              </select>
            </div>
          </div>

          {isResident ? (
            <>
              <div>
                <Label>Residente *</Label>
                <SearchableSelect
                  value={form.resident_id}
                  onChange={(rid) => { setForm({ ...form, resident_id: rid }); loadResidentPending?.(rid); }}
                  options={residents.map(r => ({ value: r.resident_id, label: r.full_name, hint: r.dni ? `DNI ${r.dni}` : undefined }))}
                  placeholder="Buscar por nombre o DNI..."
                  emptyLabel="Ningún residente coincide"
                  testId="collection-resident-select"
                />
                {selectedResident && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    <User className="w-3 h-3 inline mr-1" />
                    Se aplicará a la Cuenta Corriente de <strong>{selectedResident.full_name}</strong>. Si no hay factura pendiente, quedará como <strong>saldo a favor</strong> (anticipo) para próximas FCs.
                  </div>
                )}
              </div>

              {form.resident_id && (
                <div className="rounded-md border p-3 space-y-2" data-testid="resident-pending-invoices">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Comprobantes de la cuenta corriente</Label>
                    {residentPending.available_advance > 0 && (
                      <Badge variant="outline" className="border-emerald-300 text-emerald-700" data-testid="resident-advance-badge">
                        Saldo a favor: {fmtCurrency(residentPending.available_advance)}
                      </Badge>
                    )}
                  </div>
                  {residentPending.invoices.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Este residente no tiene facturas con saldo pendiente. La cobranza quedará como saldo a favor.</p>
                  ) : (
                    <>
                      {residentPending.invoices.map(inv => (
                        <div key={inv.invoice_id} className="flex items-center gap-2 text-sm" data-testid={`res-pending-${inv.invoice_id}`}>
                          <input type="checkbox" className="h-4 w-4" checked={inv.selected}
                            onChange={() => toggleResidentInvoice?.(inv.invoice_id)}
                            data-testid={`res-pending-check-${inv.invoice_id}`} />
                          <span className="flex-1">
                            <span className="font-mono text-xs">{inv.invoice_number}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              Vto. {(inv.due_date || inv.issue_date || '').slice(0, 10).split('-').reverse().join('/')} · saldo {fmtCurrency(inv.outstanding)}
                              {inv.credited_amount > 0 ? ` · acreditado ${fmtCurrency(inv.credited_amount)}` : ''}
                            </span>
                          </span>
                          <MoneyInput className="h-8 w-32" value={inv.payAmount} disabled={!inv.selected}
                            onChange={(e) => updateResidentInvoiceAmount?.(inv.invoice_id, e.target.value)}
                            data-testid={`res-pending-amount-${inv.invoice_id}`} />
                        </div>
                      ))}
                      <div className="flex justify-between text-sm pt-1 border-t">
                        <span className="text-muted-foreground">Deuda total: {fmtCurrency(residentPending.total_outstanding)}</span>
                        <span className="font-semibold" data-testid="resident-alloc-total">A imputar: {fmtCurrency(residentAllocTotal)}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Si no marcás ningún comprobante, el cobro se imputa automáticamente a las facturas más antiguas primero. El excedente queda como saldo a favor.
                      </p>
                    </>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <Label>Concepto de Cobranza *</Label>
                <select value={form.income_concept_id} onChange={(e) => setForm({ ...form, income_concept_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="">Seleccionar...</option>
                  {incomeConcepts.map(ic => <option key={ic.concept_id} value={ic.concept_id}>{ic.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Cliente (opcional)</Label>
                <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Nombre del cliente" />
              </div>
            </>
          )}

          <div>
            <Label>Descripción</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>

          <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-3">
              <Label className="text-base font-semibold">Medios de Pago</Label>
              <Button type="button" variant="outline" size="sm" onClick={addPaymentDetail}>
                <Plus className="w-4 h-4 mr-1" />Agregar
              </Button>
            </div>
            {form.payment_details.map((pd, index) => (
              <div key={index} className="mb-3 p-3 bg-muted/30 rounded-lg">
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-4">
                    <Label className="text-xs">Tipo *</Label>
                    <select value={pd.payment_type_id} onChange={(e) => updatePaymentDetail(index, 'payment_type_id', e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm">
                      <option value="">Seleccionar...</option>
                      {availablePaymentTypesColl.map(pt => <option key={pt.payment_type_id} value={pt.payment_type_id}>{pt.name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Monto *</Label>
                    <MoneyInput value={pd.amount} onChange={(e) => updatePaymentDetail(index, 'amount', e.target.value)} placeholder="$ 0,00" className="h-9" />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">Referencia</Label>
                    <Input value={pd.reference} onChange={(e) => updatePaymentDetail(index, 'reference', e.target.value)} placeholder="Nro cheque, transf..." className="h-9" />
                  </div>
                  <div className="col-span-1">
                    {form.payment_details.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removePaymentDetail(index)} className="h-9 w-9 text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {isCheckPaymentType(pd.payment_type_id) && (
                  <div className="mt-2">
                    <Label className="text-xs text-amber-700 font-semibold">Fecha de Vencimiento *</Label>
                    <Input
                      type="date"
                      value={pd.due_date || ''}
                      onChange={(e) => updatePaymentDetail(index, 'due_date', e.target.value)}
                      className="h-9 max-w-[200px] border-amber-300"
                      data-testid={`payment-due-date-${index}`}
                    />
                  </div>
                )}
                {isTransferPaymentType(pd.payment_type_id) && (
                  <div className="mt-2">
                    <Label className="text-xs text-sky-700 font-semibold">
                      Fecha valor (acreditación en banco)
                    </Label>
                    <Input
                      type="date"
                      value={pd.value_date || ''}
                      onChange={(e) => updatePaymentDetail(index, 'value_date', e.target.value)}
                      className="h-9 max-w-[200px] border-sky-300"
                      data-testid={`payment-value-date-${index}`}
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Si queda vacía se usa la fecha del recibo. El asiento contable siempre respeta la fecha del recibo.
                    </p>
                    {pd.value_date && form.date && pd.value_date < form.date && (
                      <p className="text-[10px] text-amber-700 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> La fecha valor es anterior al recibo.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="text-right mt-3 pt-3 border-t">
              <span className="text-lg font-bold">Total: {fmtCurrency(collectionTotal)}</span>
            </div>
            {form.payer_type === 'resident' && residentAllocTotal > 0 && (
              <div className={`mt-3 p-3 rounded-md border text-sm flex items-start gap-2 ${
                allocOver ? 'border-rose-300 bg-rose-50 text-rose-800'
                : Math.abs(allocDiff) <= 0.009 ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                : 'border-amber-300 bg-amber-50 text-amber-900'}`} data-testid="alloc-difference-alert">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span className="flex-1">
                  <span className="block font-semibold">
                    Imputado {fmtCurrency(residentAllocTotal)} · Recibido {fmtCurrency(collectionTotal)} ·
                    Diferencia <span data-testid="alloc-difference-value">{fmtCurrency(Math.abs(allocDiff))}</span>
                  </span>
                  {allocOver
                    ? 'Estás imputando más de lo que recibís. Bajá la imputación o agregá el valor faltante.'
                    : Math.abs(allocDiff) <= 0.009
                    ? 'La imputación coincide exactamente con los valores recibidos.'
                    : `Sobran ${fmtCurrency(Math.abs(allocDiff))} de los valores recibidos: se imputarán al resto de la deuda (más antigua primero) y el excedente quedará como saldo a favor.`}
                </span>
              </div>
            )}
            {(() => {
              const conflict = detectCashIntoBankConflict(form.cash_bank_id, form.payment_details);
              return conflict ? (
                <div className="mt-3 p-3 rounded-md border border-rose-300 bg-rose-50 text-sm text-rose-800 flex items-start gap-2" data-testid="cash-bank-conflict-collection">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{conflict.message}</span>
                </div>
              ) : null;
            })()}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancelar</Button>
          <Button
            onClick={onSave}
            disabled={submitting || !!detectCashIntoBankConflict(form.cash_bank_id, form.payment_details) || allocOver}
            data-testid="save-collection-btn"
          >{submitting ? 'Registrando…' : 'Registrar Cobranza'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PaymentOrderDialog({
  open, onOpenChange, form, setForm, cashBanks, suppliers, expenseConcepts, paymentTypes,
  supplierPending, setSupplierPending, loadSupplierPending, togglePendingSelect, updatePendingAmount,
  // v3.38.3 — Saldo a favor del proveedor + helper para aplicarlo
  supplierAdvanceTotal = 0, supplierNcCreditTotal = 0, applySupplierAdvanceAuto,
  debtAllocationsTotal, addPOPaymentDetail, updatePOPaymentDetail, removePOPaymentDetail,
  isDeferredCheckPaymentType, isCashPaymentType = () => false,
  detectCashIntoBankConflict, onSave, onCancel,
  submitting = false,
}) {
  const [applyingAdvance, setApplyingAdvance] = useState(false);
  // v3.36.0 — Filtrar payment_types según categorías permitidas del cash_bank seleccionado
  const selectedCashBank = cashBanks.find(cb => cb.cash_bank_id === form.cash_bank_id);
  const allowedCats = selectedCashBank?.allowed_payment_categories ?? null;
  const availablePaymentTypes = (paymentTypes || []).filter(pt => {
    if (allowedCats === null) return true;        // sin restricciones
    if (allowedCats.length === 0) return false;   // bloqueado explícito
    return allowedCats.includes((pt.category || '').toLowerCase());
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowDownCircle className="w-5 h-5 text-red-500" />
            {form.is_advance ? 'Nuevo Anticipo a rendir' : 'Nueva Orden de Pago'}
            {form.check_id && (
              <Badge className="bg-blue-100 text-blue-800 ml-2">Desde Cheque en Cartera</Badge>
            )}
          </DialogTitle>
          <DialogDescription>Registrá un egreso de dinero (pago o anticipo a rendir) y sus medios de pago.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fecha *</Label>
              <Input type="date" max={today()} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label>Caja/Banco origen *</Label>
              <select value={form.cash_bank_id} onChange={(e) => setForm({ ...form, cash_bank_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">Seleccionar...</option>
                {cashBanks.map(cb => <option key={cb.cash_bank_id} value={cb.cash_bank_id}>{cb.name} ({cb.currency})</option>)}
              </select>
            </div>
          </div>
          <label className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 cursor-pointer" data-testid="advance-toggle">
            <input type="checkbox" className="mt-0.5 h-4 w-4" checked={form.is_advance}
              onChange={(e) => setForm({ ...form, is_advance: e.target.checked, ...(e.target.checked ? { recipient_type: 'other', supplier_id: '', cancel_supplier_debt: false } : {}) })} />
            <span className="text-sm">
              <span className="font-semibold flex items-center gap-1"><HandCoins className="w-4 h-4 text-amber-600" /> Es un Anticipo a rendir</span>
              <span className="text-muted-foreground">Entregás dinero por adelantado (ej: para una compra). No se imputa como gasto todavía: queda como "Anticipo a rendir" hasta que cargues la <strong>rendición y cierre</strong> con los gastos reales.</span>
            </span>
          </label>
          <div className="border-t pt-4">
            <Label className="text-base font-semibold mb-3 block">Destinatario</Label>
            <div className="flex gap-4 mb-3">
              {!form.is_advance && (
                <label className="flex items-center gap-2">
                  <input type="radio" name="po_recipient_type" value="supplier" checked={form.recipient_type === 'supplier'} onChange={(e) => setForm({ ...form, recipient_type: e.target.value })} />
                  <Building2 className="w-4 h-4" /> Proveedor
                </label>
              )}
              <label className="flex items-center gap-2">
                <input type="radio" name="po_recipient_type" value="other" checked={form.recipient_type === 'other'} onChange={(e) => { setForm({ ...form, recipient_type: e.target.value, cancel_supplier_debt: false, payroll_data: null }); setSupplierPending([]); }} />
                <User className="w-4 h-4" /> {form.is_advance ? 'Persona / Responsable' : 'Otro'}
              </label>
              {!form.is_advance && (
                <label className="flex items-center gap-2">
                  <input type="radio" name="po_recipient_type" value="payroll" checked={form.recipient_type === 'payroll'} onChange={(e) => { setForm({ ...form, recipient_type: e.target.value, cancel_supplier_debt: false, supplier_id: '' }); setSupplierPending([]); }} data-testid="po-recipient-payroll" />
                  <Users className="w-4 h-4 text-violet-600" /> Nómina de Sueldos
                </label>
              )}
            </div>

            {form.recipient_type === 'supplier' ? (
              <SearchableSelect
                value={form.supplier_id}
                onChange={(sid) => { setForm({ ...form, supplier_id: sid }); if (form.cancel_supplier_debt) loadSupplierPending(sid); }}
                options={suppliers.map(s => ({ value: s.supplier_id, label: s.business_name, hint: s.tax_id ? `CUIT ${s.tax_id}` : undefined }))}
                placeholder="Buscar proveedor por nombre o CUIT..."
                emptyLabel="Ningún proveedor coincide"
                testId="po-supplier-select"
              />
            ) : form.recipient_type === 'payroll' ? (
              <div className="space-y-3">
                <Input
                  value={form.recipient_name}
                  onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                  placeholder='Descripción del lote (ej. "Sueldos Setiembre 2026")'
                  data-testid="po-payroll-name"
                />
                <PayrollUploader
                  opTotal={form.payment_details.reduce((s, pd) => s + (parseFloat(pd.amount) || 0), 0)}
                  onChange={(payload) => setForm(prev => ({ ...prev, payroll_data: payload }))}
                />
              </div>
            ) : (
              <Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} placeholder="Nombre del destinatario" />
            )}

            {form.recipient_type === 'supplier' && !form.is_advance && (
              <label className="flex items-start gap-3 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20 p-3 cursor-pointer mt-3" data-testid="cancel-debt-toggle">
                <input type="checkbox" className="mt-0.5 h-4 w-4" checked={form.cancel_supplier_debt}
                  onChange={(e) => { const on = e.target.checked; setForm({ ...form, cancel_supplier_debt: on, ...(on ? { expense_concept_id: '' } : {}) }); if (on) loadSupplierPending(form.supplier_id); else setSupplierPending([]); }} />
                <span className="text-sm">
                  <span className="font-semibold flex items-center gap-1"><Wallet className="w-4 h-4 text-emerald-600" /> Cancelar deuda de cuenta corriente</span>
                  <span className="text-muted-foreground">Imputá el pago a comprobantes pendientes del proveedor. Se contabiliza como baja de "Proveedores a pagar" (no como gasto nuevo).</span>
                </span>
              </label>
            )}

            {form.cancel_supplier_debt && form.supplier_id && (
              <div className="mt-3 rounded-md border p-3 space-y-2" data-testid="pending-invoices-list">
                <Label className="text-sm font-semibold">Comprobantes pendientes</Label>

                {/* v3.38.3 — Aviso de saldo a favor del proveedor (anticipos) + botón para netear */}
                {supplierAdvanceTotal > 0.009 && (
                  <div
                    className="rounded-md border border-amber-300 bg-amber-50 p-2.5 flex items-start gap-2 text-sm"
                    data-testid="po-advance-banner-apply"
                  >
                    <HandCoins className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
                    <div className="flex-1">
                      <div className="font-semibold text-amber-900">
                        Este proveedor tiene <span data-testid="po-advance-amount">{fmtCurrency(supplierAdvanceTotal)}</span> a favor
                      </div>
                      <div className="text-xs text-amber-800">
                        Aplicá el saldo primero y los importes de las facturas se van a mostrar neteados.
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-amber-400 text-amber-900 hover:bg-amber-100"
                      disabled={applyingAdvance || !applySupplierAdvanceAuto}
                      onClick={async () => {
                        if (!applySupplierAdvanceAuto) return;
                        setApplyingAdvance(true);
                        try { await applySupplierAdvanceAuto(form.supplier_id); }
                        finally { setApplyingAdvance(false); }
                      }}
                      data-testid="po-apply-advance-btn"
                    >
                      {applyingAdvance ? 'Aplicando…' : 'Aplicar al pago'}
                    </Button>
                  </div>
                )}

                {/* v3.38.3 — NCs sin netear: solo informativo */}
                {supplierNcCreditTotal > 0.009 && (
                  <div
                    className="rounded-md border border-sky-200 bg-sky-50/70 p-2 flex items-start gap-2 text-xs"
                    data-testid="po-nc-notice"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-sky-700 shrink-0" />
                    <span className="text-sky-900">
                      Hay <b>{fmtCurrency(supplierNcCreditTotal)}</b> en Notas de Crédito del proveedor sin netear.
                      Si corresponde descontarlas, usá <b>Cuenta Corriente → Aplicar a comprobantes</b>.
                    </span>
                  </div>
                )}

                {supplierPending.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Este proveedor no tiene comprobantes pendientes en cuenta corriente. El pago quedará como <strong>saldo a favor</strong>.</p>
                ) : supplierPending.map(p => (
                  <div key={p.purchase_invoice_id} className="flex items-center gap-2 text-sm" data-testid={`pending-${p.purchase_invoice_id}`}>
                    <input type="checkbox" className="h-4 w-4" checked={p.selected} onChange={() => togglePendingSelect(p.purchase_invoice_id)} data-testid={`pending-check-${p.purchase_invoice_id}`} />
                    <span className="flex-1">{p.label} <span className="text-xs text-muted-foreground">(saldo {fmtCurrency(p.outstanding)})</span></span>
                    <MoneyInput className="h-8 w-32" value={p.payAmount} disabled={!p.selected} onChange={(e) => updatePendingAmount(p.purchase_invoice_id, e.target.value)} data-testid={`pending-amount-${p.purchase_invoice_id}`} />
                  </div>
                ))}
                {supplierPending.length > 0 && (
                  <div className="text-right text-sm font-semibold pt-1 border-t" data-testid="debt-allocations-total">Total a cancelar: {fmtCurrency(debtAllocationsTotal)}</div>
                )}
                {(() => {
                  const payTot = form.payment_details.reduce((s, pd) => s + (parseFloat(pd.amount) || 0), 0);
                  const advance = Math.round((payTot - debtAllocationsTotal) * 100) / 100;
                  if (advance > 0.009 && payTot > 0) {
                    return (
                      <div className="text-sm bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-1" data-testid="po-advance-banner">
                        💰 Sobran <strong>{fmtCurrency(advance)}</strong>: quedará como <strong>saldo a favor</strong> del proveedor para aplicar a próximos comprobantes.
                      </div>
                    );
                  }
                  if (advance < -0.009) {
                    return (
                      <div className="text-sm bg-red-50 border border-red-200 rounded px-3 py-2 mt-1" data-testid="po-overalloc-banner">
                        ⚠️ Lo asignado a comprobantes <strong>{fmtCurrency(debtAllocationsTotal)}</strong> supera el total pagado <strong>{fmtCurrency(payTot)}</strong>.
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
          </div>
          {!form.cancel_supplier_debt && (
            <div>
              <Label>Concepto de Gasto {form.is_advance ? <span className="text-muted-foreground font-normal">(opcional — destino estimado)</span> : '*'}</Label>
              <SearchableSelect
                value={form.expense_concept_id || ''}
                onChange={(val) => setForm({ ...form, expense_concept_id: val })}
                options={[...expenseConcepts]
                  .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }))
                  .map(ec => ({ value: ec.concept_id, label: ec.name }))}
                placeholder={form.is_advance ? 'Sin imputar (se define al rendir)' : 'Seleccionar concepto...'}
                testId="po-expense-concept"
              />
            </div>
          )}

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base font-semibold">Medios de Pago</Label>
              <Button type="button" variant="outline" size="sm" onClick={addPOPaymentDetail}>
                <Plus className="w-3 h-3 mr-1" /> Agregar
              </Button>
            </div>
            {form.payment_details.map((pd, index) => (
              <div key={index} className={`mb-3 p-3 rounded-lg ${pd.locked ? 'bg-blue-50 border border-blue-200' : 'bg-muted/30'}`}>
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-4">
                    <Label className="text-xs">Tipo de Valor *</Label>
                    <select
                      value={pd.payment_type_id}
                      onChange={(e) => updatePOPaymentDetail(index, 'payment_type_id', e.target.value)}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                      disabled={pd.locked}
                    >
                      <option value="">Seleccionar...</option>
                      {availablePaymentTypes.map(pt => <option key={pt.payment_type_id} value={pt.payment_type_id}>{pt.name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Monto *</Label>
                    <MoneyInput
                      value={pd.amount}
                      onChange={(e) => updatePOPaymentDetail(index, 'amount', e.target.value)}
                      placeholder="$ 0,00" className="h-9"
                      disabled={pd.locked}
                    />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">Referencia</Label>
                    <Input
                      value={pd.reference}
                      onChange={(e) => updatePOPaymentDetail(index, 'reference', e.target.value)}
                      placeholder="Nro cheque, transf..." className="h-9"
                      disabled={pd.locked}
                    />
                  </div>
                  <div className="col-span-1">
                    {!pd.locked && form.payment_details.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removePOPaymentDetail(index)} className="h-9 w-9 text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {pd.locked && (
                  <p className="text-xs text-blue-600 mt-1">Cheque pre-cargado desde Valores en Cartera</p>
                )}
                {isDeferredCheckPaymentType(pd.payment_type_id) && !pd.locked && (
                  <div className="mt-2">
                    <Label className="text-xs text-amber-700 font-semibold">Fecha de pago del cheque diferido / e.Cheq *</Label>
                    <Input
                      type="date"
                      value={pd.due_date || ''}
                      onChange={(e) => updatePOPaymentDetail(index, 'due_date', e.target.value)}
                      className="h-9 max-w-[200px] border-amber-300"
                      data-testid={`po-payment-due-date-${index}`}
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">Se generará un valor en VALORES (estado Pendiente) y no moverá fondos hasta marcarlo como Pagado.</p>
                  </div>
                )}
                {pd.payment_type_id && !isCashPaymentType(pd.payment_type_id) && !isDeferredCheckPaymentType(pd.payment_type_id) && !pd.locked && (
                  <div className="mt-2">
                    <Label className="text-xs text-sky-700 font-semibold">Fecha valor (débito en banco)</Label>
                    <Input
                      type="date"
                      value={pd.value_date || ''}
                      onChange={(e) => updatePOPaymentDetail(index, 'value_date', e.target.value)}
                      className="h-9 max-w-[200px] border-sky-300"
                      data-testid={`po-payment-value-date-${index}`}
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Si queda vacía se usa la fecha de la OP. El asiento contable siempre respeta la fecha del comprobante.
                    </p>
                    {pd.value_date && form.date && pd.value_date < form.date && (
                      <p className="text-[10px] text-amber-700 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> La fecha valor es anterior a la OP.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="text-right text-sm font-semibold mt-2">
              Total: {fmtCurrency(form.payment_details.reduce((s, pd) => s + (parseFloat(pd.amount) || 0), 0))}
            </div>
          </div>

          <div>
            <Label>Descripcion</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>
          {(() => {
            const conflict = detectCashIntoBankConflict(form.cash_bank_id, form.payment_details);
            return conflict ? (
              <div className="p-3 rounded-md border border-rose-300 bg-rose-50 text-sm text-rose-800 flex items-start gap-2" data-testid="cash-bank-conflict-po">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{conflict.message}</span>
              </div>
            ) : null;
          })()}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancelar</Button>
          <Button
            onClick={onSave}
            disabled={submitting || !!detectCashIntoBankConflict(form.cash_bank_id, form.payment_details)}
            data-testid="save-po-btn"
          >{submitting ? 'Registrando…' : (form.is_advance ? 'Registrar Anticipo' : 'Registrar Pago')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
